import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { redeemInvite } from "@/lib/api-client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Building2, Loader2, ArrowRight } from "lucide-react";
import { normalizeWebsiteUrl, validateWebsite } from "@/lib/validation";
import {
  AddressAutocomplete,
  composeAddressDisplay,
} from "@/components/ui/address-autocomplete";
import type { StructuredAddress } from "@/components/ui/address-autocomplete";
import { API_BASE_URL } from "@/lib/api-base";

const API_BASE = API_BASE_URL;

interface DomainMatch {
  match: boolean;
  company_name: string | null;
  company_id: string | null;
}

export default function OnboardingPage() {
  const { t } = useTranslation("onboarding");
  const { user, session, refreshProfile } = useAuth();
  const navigate = useNavigate();

  const [domainMatch, setDomainMatch] = useState<DomainMatch | null>(null);
  const [checking, setChecking] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Company creation form
  const [companyForm, setCompanyForm] = useState({
    name: "",
    company_street: "",
    company_postal_code: "",
    company_city: "",
    company_country: "DE",
    company_address_extra: "",
    company_address_display: "",
    website: "",
  });

  // On mount: try invite redemption first, then fall back to domain check
  useEffect(() => {
    if (!user?.email) return;

    let cancelled = false;

    async function init() {
      // 1. If user signed up via invite link, try to redeem it
      const inviteToken = user!.user_metadata?.invite_token;
      if (inviteToken) {
        try {
          await redeemInvite(inviteToken);
          if (cancelled) return;
          await refreshProfile();
          navigate("/help", { replace: true });
          return; // Success — skip domain check entirely
        } catch {
          // Invite invalid/expired/used — continue to domain check
        }
      }

      // 2. No invite or redemption failed — check domain match
      if (cancelled) return;
      try {
        const res = await fetch(`${API_BASE}/auth/check-domain`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: user!.email }),
        });
        if (!cancelled && res.ok) {
          const data: DomainMatch = await res.json();
          setDomainMatch(data);
        }
      } catch {
        // Non-critical — just show creation form
      } finally {
        if (!cancelled) setChecking(false);
      }
    }

    init();
    return () => {
      cancelled = true;
    };
  }, [user?.email, refreshProfile, navigate]);

  async function handleJoinCompany() {
    if (!domainMatch?.company_id || !session?.access_token) return;
    setError(null);
    setLoading(true);

    try {
      const res = await fetch(
        `${API_BASE}/companies/${domainMatch.company_id}/join`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${session.access_token}` },
        },
      );
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.detail || t("errors.joinFailed"));
      }
      await refreshProfile();
      navigate("/help", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : t("errors.joinError"));
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateCompany(e: React.FormEvent) {
    e.preventDefault();
    if (!session?.access_token) return;
    setError(null);
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/companies`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          name: companyForm.name,
          website: normalizeWebsiteUrl(companyForm.website) || null,
          company_street: companyForm.company_street,
          company_postal_code: companyForm.company_postal_code,
          company_city: companyForm.company_city,
          company_country: companyForm.company_country,
          ...(companyForm.company_address_extra
            ? { company_address_extra: companyForm.company_address_extra }
            : {}),
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.detail || t("errors.creationFailed"));
      }
      await refreshProfile();
      navigate("/help", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : t("errors.creationError"));
    } finally {
      setLoading(false);
    }
  }

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/30">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Domain match found — offer to join
  if (domainMatch?.match && domainMatch.company_name) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/30 px-4">
        <Card className="w-full max-w-md shadow-card">
          <CardHeader className="text-center">
            <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
              <Building2 className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="text-2xl">{t("domainMatch.title")}</CardTitle>
            <CardDescription>{t("domainMatch.description")}</CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <div className="rounded-lg border bg-muted/50 p-4">
              <p className="text-lg font-semibold">
                {domainMatch.company_name}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {t("domainMatch.question")}
              </p>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col gap-3">
            <Button
              className="w-full"
              onClick={handleJoinCompany}
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <ArrowRight className="mr-2 h-4 w-4" />
              )}
              {t("domainMatch.joinButton")}
            </Button>
            <Button
              variant="ghost"
              className="w-full text-muted-foreground"
              onClick={() =>
                setDomainMatch({
                  match: false,
                  company_name: null,
                  company_id: null,
                })
              }
            >
              {t("domainMatch.createNewButton")}
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // No match — company creation form
  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 px-4">
      <Card className="w-full max-w-md shadow-card">
        <CardHeader className="text-center">
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
            <Building2 className="h-6 w-6 text-primary" />
          </div>
          <CardTitle className="text-2xl">{t("creation.title")}</CardTitle>
          <CardDescription>{t("creation.description")}</CardDescription>
        </CardHeader>
        <form onSubmit={handleCreateCompany}>
          <CardContent className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <div className="space-y-2">
              <Label htmlFor="name">{t("form.name.label")}</Label>
              <Input
                id="name"
                value={companyForm.name}
                onChange={(e) =>
                  setCompanyForm((p) => ({ ...p, name: e.target.value }))
                }
                required
                placeholder={t("form.name.placeholder")}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="website">{t("form.website.label")}</Label>
              <Input
                id="website"
                type="text"
                value={companyForm.website}
                onChange={(e) =>
                  setCompanyForm((p) => ({ ...p, website: e.target.value }))
                }
                placeholder={t("form.website.placeholder")}
              />
              {companyForm.website && validateWebsite(companyForm.website) && (
                <p className="text-xs text-destructive">
                  {validateWebsite(companyForm.website)}
                </p>
              )}
            </div>

            {/* Single address typeahead */}
            <div className="space-y-2">
              <Label htmlFor="company_address">{t("form.address.label")}</Label>
              <AddressAutocomplete
                id="company_address"
                value={companyForm.company_address_display}
                onChange={(val) =>
                  setCompanyForm((p) => ({
                    ...p,
                    company_address_display: val,
                  }))
                }
                onStructuredChange={(addr: StructuredAddress) => {
                  const display = composeAddressDisplay(addr);
                  setCompanyForm((p) => ({
                    ...p,
                    company_street: addr.street,
                    company_postal_code: addr.postalCode,
                    company_city: addr.city,
                    company_country: addr.country || "DE",
                    ...(display ? { company_address_display: display } : {}),
                  }));
                }}
                structuredValue={{
                  street: companyForm.company_street,
                  postalCode: companyForm.company_postal_code,
                  city: companyForm.company_city,
                  country: companyForm.company_country,
                }}
                placeholder={t("form.address.placeholder")}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="company_address_extra">
                {t("form.addressExtra.label")}
              </Label>
              <Input
                id="company_address_extra"
                value={companyForm.company_address_extra}
                onChange={(e) =>
                  setCompanyForm((p) => ({
                    ...p,
                    company_address_extra: e.target.value,
                  }))
                }
                placeholder={t("form.addressExtra.placeholder")}
              />
            </div>
          </CardContent>
          <CardFooter>
            <Button
              type="submit"
              className="w-full"
              disabled={
                loading ||
                !companyForm.name.trim() ||
                !companyForm.company_street.trim() ||
                !companyForm.company_postal_code.trim() ||
                !companyForm.company_city.trim()
              }
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t("form.submitButton")}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
